
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LanguageGuard from '../../components/feature/LanguageGuard';
import SeoHead from '../../components/feature/SeoHead';
import { getLangPath, type Language } from '../../router/langRoutes';
import HeroSection from './components/HeroSection';
import IntroSection from './components/IntroSection';
import BenefitsSection from './components/BenefitsSection';
import TargetSection from './components/TargetSection';
import WhyPartnerSection from './components/WhyPartnerSection';
import ProcessSection from './components/ProcessSection';
import FormSection from './components/FormSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Hợp Tác Kinh Doanh & Đầu Tư Định Cư Latvia - The Daeg International',
    description:
      'Cơ hội hợp tác kinh doanh và đầu tư định cư Latvia cùng The Daeg International. Đầu tư vào Asian Alley, nhận thẻ tạm trú châu Âu, lợi nhuận hấp dẫn. Tìm kiếm đối tác đầu tư định cư Latvia tại Việt Nam.',
    keywords:
      'hợp tác định cư Latvia, đầu tư kinh doanh Latvia, đối tác định cư Latvia, cơ hội đầu tư Latvia, hợp tác Asian Alley, đầu tư siêu thị Latvia',
  },
  en: {
    title: 'Business Partnership & Latvia Residency Investment - The Daeg International',
    description:
      'Business partnership and Latvia residency investment opportunities with The Daeg International. Invest in Asian Alley, obtain European residence card, attractive returns.',
    keywords:
      'Latvia residency partnership, Latvia business investment, Latvia residency partner, Asian Alley partnership, Latvia supermarket investment',
  },
  lv: {
    title: 'Biznesa Partnerība & Latvijas Uzturēšanās Ieguldījumi - The Daeg International',
    description:
      'Biznesa partnerības un Latvijas uzturēšanās ieguldījumu iespējas ar The Daeg International. Ieguldiet Asian Alley, saņemiet Eiropas uzturēšanās atļauju.',
    keywords:
      'Latvijas uzturēšanās partnerība, biznesa ieguldījumi Latvijā, Asian Alley partnerība',
  },
};

const PartnershipPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { i18n } = useTranslation();
  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  const partnershipSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Dịch vụ Hợp Tác Đầu Tư Định Cư Latvia',
    provider: {
      '@type': 'Organization',
      name: 'The Daeg International Trade',
      url: SITE_URL,
    },
    description:
      'Cơ hội hợp tác kinh doanh và đầu tư định cư Latvia thông qua Asian Alley. Nhận thẻ tạm trú châu Âu với mức đầu tư từ 50.000 EUR.',
    url: `${SITE_URL}${getLangPath('latviaPartnership', lang)}`,
    areaServed: [
      { '@type': 'Country', name: 'Vietnam' },
      { '@type': 'Country', name: 'Latvia' },
    ],
    offers: {
      '@type': 'Offer',
      price: '50000',
      priceCurrency: 'EUR',
      description: 'Đầu tư hợp tác định cư Latvia từ 50.000 EUR',
    },
  };

  return (
    <LanguageGuard>
      <div className="min-h-screen bg-white">
        <SeoHead
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          canonicalPath={getLangPath('latviaPartnership', lang)}
          schemaJson={partnershipSchema}
        />
        <Navbar onConsultClick={() => setIsModalOpen(true)} />
        <main>
          <h1 className="sr-only">{seo.title}</h1>
          <HeroSection />
          <IntroSection />
          <BenefitsSection />
          <TargetSection />
          <WhyPartnerSection />
          <ProcessSection />
          <FormSection />
        </main>
        <Footer />
        <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <ScrollToTop />
      </div>
    </LanguageGuard>
  );
};

export default PartnershipPage;
